import React, { FC, useRef, useState, useEffect } from '../../lib/teact/teact';
import { requestMutation } from '../../lib/fasterdom/fasterdom';
import buildClassName from '../../util/buildClassName';

import styles from './PullToRefresh.module.scss';

type OwnProps = {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  className?: string;
};

const MAX_PULL = 120;
const REFRESH_THRESHOLD = 80;

const PullToRefresh: FC<OwnProps> = ({ onRefresh, children, className }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef<HTMLDivElement>();
  const contentRef = useRef<HTMLDivElement>();
  
  const startYRef = useRef<number>(0);
  const currentYRef = useRef<number>(0);
  const isPullingRef = useRef<boolean>(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (container.scrollTop > 0 || isRefreshing) return;
      isPullingRef.current = true;
      startYRef.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPullingRef.current) return;
      if (container.scrollTop > 0) {
        isPullingRef.current = false;
        return;
      }
      
      const y = e.touches[0].clientY;
      const deltaY = y - startYRef.current;

      if (deltaY > 0) {
        // Prevent default native scroll behavior (overscroll glow)
        if (e.cancelable) e.preventDefault();
        
        currentYRef.current = Math.min(deltaY * 0.5, MAX_PULL); // Adding friction
        
        requestMutation(() => {
          if (contentRef.current) {
            contentRef.current.style.transform = `translateY(${currentYRef.current}px)`;
          }
        });
      }
    };

    const handleTouchEnd = () => {
      if (!isPullingRef.current) return;
      isPullingRef.current = false;

      if (currentYRef.current >= REFRESH_THRESHOLD) {
        setIsRefreshing(true);
        // Instantly snap back and trigger refresh
        requestMutation(() => {
          if (contentRef.current) {
            contentRef.current.style.transition = 'transform 0.3s ease';
            contentRef.current.style.transform = 'translateY(0)';
            setTimeout(() => {
              if (contentRef.current) contentRef.current.style.transition = '';
            }, 300);
          }
        });

        onRefresh().finally(() => {
          setIsRefreshing(false);
        });
      } else {
        requestMutation(() => {
          if (contentRef.current) {
            contentRef.current.style.transition = 'transform 0.3s ease';
            contentRef.current.style.transform = 'translateY(0)';
            setTimeout(() => {
              if (contentRef.current) contentRef.current.style.transition = '';
            }, 300);
          }
        });
      }
      currentYRef.current = 0;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isRefreshing, onRefresh]);

  return (
    <div ref={containerRef} className={buildClassName(styles.root, className)}>
      <div ref={contentRef} className={styles.content}>
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
